import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { getClientInviteByToken, submitClientInvite } from "@/utils/client-invites.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Loader2, MailCheck, AlertCircle } from "lucide-react";

export const Route = createFileRoute("/clients/invite/$token")({
  component: PublicInvitePage,
  head: () => ({
    meta: [{ title: "Client signup" }],
  }),
});

function PublicInvitePage() {
  const { t } = useTranslation();
  const { token } = Route.useParams();
  const getInvite = useServerFn(getClientInviteByToken);
  const submit = useServerFn(submitClientInvite);

  const schema = z.object({
    name: z.string().trim().min(1, t("publicInvite.errors.name_required_zod")).max(120),
    email: z.string().trim().email(t("publicInvite.errors.email_invalid")).max(160),
    phone: z.string().trim().max(40).optional().or(z.literal("")),
    company_name: z.string().trim().max(160).optional().or(z.literal("")),
    address_line1: z.string().trim().max(160).optional().or(z.literal("")),
    address_line2: z.string().trim().max(160).optional().or(z.literal("")),
    city: z.string().trim().max(80).optional().or(z.literal("")),
    state: z.string().trim().max(40).optional().or(z.literal("")),
    postal_code: z.string().trim().max(20).optional().or(z.literal("")),
    country: z.string().trim().max(80).optional().or(z.literal("")),
    notes: z.string().trim().max(2000).optional().or(z.literal("")),
  });
  type FormValues = z.infer<typeof schema>;

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState<Awaited<ReturnType<typeof getInvite>> | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      company_name: "",
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      postal_code: "",
      country: "",
      notes: "",
    },
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getInvite({ data: { token } });
        if (cancelled) return;
        setInvite(result);
        if (result) {
          form.reset({
            name: result.prefill_name ?? "",
            email: result.prefill_email ?? "",
            phone: "",
            company_name: "",
            address_line1: "",
            address_line2: "",
            city: "",
            state: "",
            postal_code: "",
            country: "",
            notes: "",
          });
        }
      } catch (e) {
        if (!cancelled)
          setError(e instanceof Error ? e.message : t("publicInvite.errors.loadInvite"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, getInvite, form, t]);

  const messageFor = (code: string | undefined): string => {
    switch (code) {
      case "invite_not_found":
        return t("publicInvite.errors.invite_not_found");
      case "invite_expired":
        return t("publicInvite.errors.invite_expired");
      case "invite_used_or_cancelled":
        return t("publicInvite.errors.invite_used_or_cancelled");
      case "name_required":
        return t("publicInvite.errors.name_required");
      case "email_required":
        return t("publicInvite.errors.email_required");
      default:
        return t("publicInvite.errors.submit");
    }
  };

  const onSubmit = async (values: FormValues) => {
    setError(null);
    try {
      const result = await submit({ data: { token, ...values } });
      if (!result.ok) {
        setError(messageFor(result.error));
        return;
      }
      setSubmittedEmail(result.email);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("publicInvite.errors.submitGeneric"));
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!invite) {
    return (
      <CenteredCard
        icon={<AlertCircle className="h-10 w-10 text-destructive" />}
        title={t("publicInvite.invalidLinkTitle")}
        description={t("publicInvite.invalidLinkDesc")}
      />
    );
  }

  if (invite.is_expired || invite.status === "expired") {
    return (
      <CenteredCard
        icon={<AlertCircle className="h-10 w-10 text-destructive" />}
        title={t("publicInvite.expiredTitle")}
        description={t("publicInvite.expiredDesc")}
      />
    );
  }

  if (invite.status === "cancelled") {
    return (
      <CenteredCard
        icon={<AlertCircle className="h-10 w-10 text-destructive" />}
        title={t("publicInvite.cancelledTitle")}
        description={t("publicInvite.cancelledDesc")}
      />
    );
  }

  if (invite.status === "confirmed") {
    return (
      <CenteredCard
        icon={<MailCheck className="h-10 w-10 text-primary" />}
        title={t("publicInvite.confirmedTitle")}
        description={t("publicInvite.confirmedDesc", { company: invite.company_name })}
      />
    );
  }

  if (submittedEmail || invite.status === "submitted") {
    return (
      <CenteredCard
        icon={<MailCheck className="h-10 w-10 text-primary" />}
        title={t("publicInvite.confirmedTitle")}
        description={t("publicInvite.confirmedDesc", { company: invite.company_name })}
      />
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <Card>
          <CardHeader className="text-center">
            {invite.company_logo_url ? (
              <img
                src={invite.company_logo_url}
                alt={invite.company_name}
                className="mx-auto mb-3 h-12 w-auto"
              />
            ) : null}
            <CardTitle>{t("publicInvite.title")}</CardTitle>
            <CardDescription>
              {t("publicInvite.description")} <strong>{invite.company_name}</strong>.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error ? (
              <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            ) : null}
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("publicInvite.fullName")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("publicInvite.namePlaceholder")} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="company_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("publicInvite.companyName")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("publicInvite.companyPlaceholder")} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("publicInvite.email")}</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder={t("publicInvite.emailPlaceholder")}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("publicInvite.phone")}</FormLabel>
                        <FormControl>
                          <Input placeholder="(11) 99999-9999" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="address_line1"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("publicInvite.address")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("publicInvite.addressPlaceholder")} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address_line2"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("publicInvite.complement")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("publicInvite.complementPlaceholder")} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid gap-4 sm:grid-cols-3">
                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("publicInvite.city")}</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("publicInvite.state")}</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="postal_code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("publicInvite.postalCode")}</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("publicInvite.country")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("publicInvite.countryPlaceholder")} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("publicInvite.notes")}</FormLabel>
                      <FormControl>
                        <Textarea
                          rows={3}
                          placeholder={t("publicInvite.notesPlaceholder")}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
                  {form.formState.isSubmitting
                    ? t("publicInvite.sending")
                    : t("publicInvite.submit")}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
        <p className="mt-4 text-center text-xs text-muted-foreground">
          {t("publicInvite.afterSubmit")}
        </p>
      </div>
    </div>
  );
}

function CenteredCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <div className="mx-auto mb-2">{icon}</div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <Link to="/" className="text-sm text-primary underline">
            {t("publicInvite.backHome")}
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
